import { Directive, inject, OnInit } from '@angular/core';
import { every } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEqual } from 'lodash';
import { combineLatest, NEVER } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { CuboidState3D } from '../../state/cuboid-state-3d';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { isPointCloseTo } from '../point-utils';
import { CuboidRescaleService } from './cuboid-rescale.service';

@Directive()
export abstract class AbstractRescaleToggle
  extends DirectiveSuperclass
  implements OnInit
{
  protected readonly store = inject(PointCloudContentStore);
  private readonly shellConnector = inject(shellConnectorToken);
  private readonly cuboidRescaleService = inject(CuboidRescaleService);

  ngOnInit() {
    this.subscribeTo(
      this.store.focusedShape$.pipe(
        distinctUntilChanged((prev, curr) => prev?.index === curr?.index),
        tap((focusedCuboid) => {
          // Set the initial value of rescaleAllFrames based
          // on the focused cuboid
          if (focusedCuboid) {
            this.store('rescaleAllFrames').set(
              hasTheSameScaleInAllFrames(focusedCuboid),
            );
          }
        }),
        switchMap((focusedCuboid) =>
          focusedCuboid
            ? combineLatest([
                this.store('rescaleAllFrames').$,
                this.store.focusedShapeLocation$.pipe(
                  distinctUntilChanged(hasTheSameKeyLocationAsPrevious),
                ),
              ])
            : NEVER,
        ),
      ),
      ([rescaleAllFrames]) => {
        const focusedShapeIndex = this.store.getFocusedShapeIndex();
        if (focusedShapeIndex && rescaleAllFrames) {
          this.cuboidRescaleService.rescaleInAllFrames(focusedShapeIndex);
        }
      },
    );
  }

  async toggleRescaleAllFrames(rescaleAll: boolean) {
    const focusedCuboidStore = this.store.getFocusedShapeStore();
    if (!focusedCuboidStore) {
      return;
    }

    const focusedCuboid = focusedCuboidStore.state();
    let rescaleResult = rescaleAll;
    if (rescaleAll && !hasTheSameScaleInAllFrames(focusedCuboid)) {
      rescaleResult =
        await this.cuboidRescaleService.rescaleInAllFramesWithConfirmation(
          focusedCuboid.index,
        );
    }
    this.store('rescaleAllFrames').set(rescaleResult);
    this.trackToggleRescaleFramesEvent(rescaleResult);
  }

  private trackToggleRescaleFramesEvent(newValue: boolean) {
    this.shellConnector.trackEvent({
      id: 'toggle_rescale_all_frames',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: newValue,
    });
  }
}

function hasTheSameScaleInAllFrames(cuboid: CuboidState3D) {
  return every(cuboid.key_locations, (keyLocation) =>
    isPointCloseTo(keyLocation.scale, cuboid.key_locations[0].scale, 0.001),
  );
}

function hasTheSameKeyLocationAsPrevious(
  prev: CuboidLocationState3D | undefined,
  curr: CuboidLocationState3D | undefined,
) {
  return (
    isEqual(prev?.scale, curr?.scale) &&
    isEqual(prev?.rotation, curr?.rotation) &&
    isEqual(prev?.position, curr?.position)
  );
}
